import React from 'react';
import {
  Check,
  ArrowRight,
  Star,
  Users,
  Target,
  Clock,
  Activity,
  BarChart3,
  Sparkles,
  ArrowLeft,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const AdvanceLeadershipTransformationMastery = () => {
  return (
    <div className="bg-exiser-light min-h-screen">
      {/* Hero */}
      <div className="bg-exiser-primary text-white py-16 md:py-20 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10" />
        <div className="max-w-5xl mx-auto px-4 relative z-10">
          <Link to="/categories" className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-6 uppercase tracking-wider text-xs font-bold transition-colors">
            <ArrowLeft className="w-4 h-4" /> Back to Programmes
          </Link>
          <div className="inline-flex items-center gap-2 bg-exiser-accent/20 border border-exiser-accent/60 px-3 py-1 rounded-full mb-4">
            <Star className="w-4 h-4 text-exiser-accent fill-current" />
            <span className="text-[10px] font-bold uppercase tracking-[0.22em] text-exiser-accent">
              Leadership Category • 3-Month Advanced System
            </span>
          </div>
          <h1 className="text-3xl md:text-5xl font-bold uppercase tracking-tight mb-4">
            Advance Leadership Transformation Mastery
          </h1>
          <p className="text-sm md:text-base text-purple-100 max-w-3xl font-sans">
            A 90-day elite leadership system for managers, leaders and agency heads who want
            powerful influence, consistent recruitment, a performance-driven team and long-term
            legacy-level growth.
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.2em]">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Duration: 3 Months</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4" />
              <span>For: Leaders / Managers / BAs / Agency Heads</span>
            </div>
            <div className="flex items-center gap-2">
              <Target className="w-4 h-4" />
              <span>Offer Fees: ₹24,999 (Regular: ₹49,999)</span>
            </div>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/categories#advance-leadership-transformation-mastery"
              className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg"
            >
              Enroll From Programmes Page
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/book"
              className="inline-flex items-center gap-2 border border-white/60 text-white px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white hover:text-exiser-primary"
            >
              Book Leadership Strategy Call
            </Link>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 space-y-10">
        {/* Why this programme */}
        <section className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 md:p-8">
          <h2 className="text-xl md:text-2xl font-bold uppercase text-exiser-primary mb-4">
            Why This 3-Month Advanced Leadership Programme?
          </h2>
          <p className="text-sm text-gray-700 font-sans mb-4">
            After basic leadership training, most leaders still struggle with real-world execution:
            recruitment inconsistency, weak team retention, low performance, and daily emotional
            pressure. This programme combines leadership psychology, systems, weekly audits and
            coaching to solve all four.
          </p>
          <ul className="grid md:grid-cols-2 gap-2 text-sm text-gray-700 font-sans">
            {[
              'Advanced leadership coaching + accountability',
              'Weekly recruitment and performance direction',
              'Deep mindset and behavioural transformation',
              'Practical systems for team handling and retention',
            ].map((item, idx) => (
              <li key={idx} className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
                {item}
              </li>
            ))}
          </ul>
        </section>

        {/* Core Systems */}
        <section className="grid md:grid-cols-2 gap-6">
          <Card
            icon={<Activity className="w-5 h-5 text-exiser-primary" />}
            title="Weekly Advanced Coaching (12 Sessions)"
            points={[
              'Leadership behaviour engineering and high-performance mindset',
              'Advisor growth psychology and resistance handling',
              'Real-time problem solving for team challenges',
              'Clarity-driven direction for the upcoming week',
            ]}
          />
          <Card
            icon={<BarChart3 className="w-5 h-5 text-exiser-primary" />}
            title="Weekly Performance Audit & Dashboard"
            points={[
              'Track team performance, recruitment and activity scores',
              'Sales conversion, pipeline health and advisor engagement',
              'Simple visual dashboard and weekly review pattern',
              'Plan the next-week strategy and focus areas',
            ]}
          />
          <Card
            icon={<Users className="w-5 h-5 text-exiser-primary" />}
            title="Powerful Team Handling & Legacy Leadership"
            points={[
              'Handling ego, emotions, conflicts and low performers',
              'Building discipline, unity and vision-based culture',
              'Developing independent leaders and mentorship layers',
              'Designing long-term leadership succession structure',
            ]}
          />
          <Card
            icon={<Sparkles className="w-5 h-5 text-exiser-primary" />}
            title="Retention, Recognition & Psychology Mastery"
            points={[
              'Advisor retention and happiness framework',
              'Monthly reward and recognition system design',
              'Weekly deep psychology sessions: stress, anxiety, fear removal',
              'Emotional stability, confidence and high-energy state activation',
            ]}
          />
        </section>

        {/* 12-Module Overview */}
        <section className="bg-white rounded-2xl shadow-md border border-purple-100 p-6 md:p-8">
          <h3 className="text-xl font-bold mb-3">
            The Complete 12-Module Leadership Transformation System
          </h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-gray-700 font-sans">
            <ModuleColumn
              heading="Month 1 — Foundation & Growth Systems"
              modules={[
                'Module 1: Leadership Psychology 2.0',
                'Module 2: Recruitment Predictability Matrix',
                'Module 3: Advisor Activation & Performance',
                'Module 4: Weekly Audit System & Team Dashboard',
              ]}
            />
            <ModuleColumn
              heading="Month 2 — Leadership Expansion"
              modules={[
                'Module 5: Emotional & Behavioural Leadership',
                'Module 6: Team Handling Mastery',
                'Module 7: Digital Leadership & Influence',
                'Module 8: Legacy Leadership Structures',
              ]}
            />
            <ModuleColumn
              heading="Month 3 — Scaling & Transformation"
              modules={[
                'Module 9: Retention & Momentum System',
                'Module 10: Recognition Psychology & Culture',
                'Module 11: Leader Identity Transformation',
                'Module 12: 90-Day Growth Expansion Blueprint',
              ]}
            />
          </div>
        </section>

        {/* Outcomes */}
        <section className="bg-exiser-primary rounded-2xl p-6 md:p-8 text-white flex flex-col gap-4">
          <h3 className="text-xl md:text-2xl font-bold uppercase">
            Who You Become in 90 Days
          </h3>
          <ul className="grid md:grid-cols-2 gap-2 text-sm text-purple-100 font-sans">
            {[
              'A powerful, influential and emotionally stable leader',
              'A consistent recruiter with a predictable pipeline',
              'A performance-driven agency builder with clear systems',
              'A respected mentor who teams follow naturally',
              'A digital-ready leader with strong personal brand',
              'A culture-builder focused on long-term legacy success',
            ].map((item, idx) => (
              <li key={idx} className="flex gap-2">
                <Check className="w-4 h-4 text-exiser-accent mt-0.5" />
                {item}
              </li>
            ))}
          </ul>

          <div className="flex flex-wrap items-center justify-between gap-4 mt-4">
            <p className="text-xs uppercase tracking-[0.22em] text-purple-200">
              Mindset • Systems • Accountability • Coaching — all in one programme
            </p>
            <Link
              to="/categories#advance-leadership-transformation-mastery"
              className="inline-flex items-center gap-2 bg-exiser-accent text-exiser-primary px-6 py-3 text-xs font-bold uppercase tracking-[0.22em] rounded-lg hover:bg-white shadow-lg"
            >
              Enroll From Programmes Page
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </div>
  );
};

const Card = ({ icon, title, points }) => (
  <div className="bg-white rounded-2xl border border-purple-100 p-6 shadow-sm">
    <div className="flex items-center gap-2 mb-3">
      {icon}
      <h3 className="text-lg font-bold">{title}</h3>
    </div>
    <ul className="space-y-2 text-sm text-gray-700 font-sans">
      {points.map((p, idx) => (
        <li key={idx} className="flex gap-2">
          <Check className="w-4 h-4 text-exiser-primary mt-0.5" />
          {p}
        </li>
      ))}
    </ul>
  </div>
);

const ModuleColumn = ({ heading, modules }) => (
  <div>
    <h4 className="text-sm font-bold text-exiser-primary mb-2">{heading}</h4>
    <ul className="space-y-1 text-xs text-gray-700 font-sans">
      {modules.map((m, idx) => (
        <li key={idx} className="flex gap-2">
          <Check className="w-3 h-3 text-exiser-primary mt-0.5" />
          {m}
        </li>
      ))}
    </ul>
  </div>
);

export default AdvanceLeadershipTransformationMastery;